import React, { useEffect, useMemo, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  CenterFocusStrong,
  Close,
  Compress,
  Expand,
  KeyboardArrowDown,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  KeyboardArrowUp,
  Lock,
  LockOpen,
  Place,
  Save,
  Search,
  UploadFile,
} from '@mui/icons-material'
import API from '../../../../services/API'
import { prepareEventMapImage } from '../../../../helpers/eventMapImage'

const defaultBounds = {
  north: 52.5205,
  south: 52.4795,
  east: -1.449,
  west: -1.551,
}

// Same footprint as the default box, but centred on the event's own map
// point — saves geocoding the venue name when the address is already known.
const boundsAround = (center) => {
  if (!Array.isArray(center) || center.length !== 2) return null
  const [lng, lat] = center.map(Number)
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null
  const halfLat = (defaultBounds.north - defaultBounds.south) / 2
  const halfLng = (defaultBounds.east - defaultBounds.west) / 2
  return {
    north: Number((lat + halfLat).toFixed(6)),
    south: Number((lat - halfLat).toFixed(6)),
    east: Number((lng + halfLng).toFixed(6)),
    west: Number((lng - halfLng).toFixed(6)),
  }
}

const toNumberBounds = (bounds = defaultBounds) => ({
  north: Number(bounds.north),
  south: Number(bounds.south),
  east: Number(bounds.east),
  west: Number(bounds.west),
})

const imageCoordinates = (bounds) => {
  const next = toNumberBounds(bounds)
  return [
    [next.west, next.north],
    [next.east, next.north],
    [next.east, next.south],
    [next.west, next.south],
  ]
}

// Free place search (OpenStreetMap Nominatim) so the plan can be jumped to the
// event's town instead of nudged across the country from the default view.
const geocodePlace = async (query) => {
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(query)}`
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!res.ok) throw new Error('Place search failed')
  const results = await res.json()
  return results.map((item) => ({
    label: item.display_name,
    lng: Number(item.lon),
    lat: Number(item.lat),
    boundingbox: item.boundingbox?.map(Number), // [south, north, west, east]
  }))
}

const CalibrationMap = ({
  imageUrl,
  bounds,
  setBounds,
  opacity,
  initialSearch,
  height = 460,
}) => {
  const mapContainer = useRef(null)
  const mapRef = useRef(null)
  const imageUrlRef = useRef(null)
  const boundsRef = useRef(bounds)
  const dragRef = useRef(null)
  // Locked = dragging pans the live map (plan stays pinned to its
  // coordinates). Unlocked = dragging moves the plan across the live map and
  // the scroll wheel resizes it.
  const [planLocked, setPlanLocked] = useState(true)
  const [searchText, setSearchText] = useState(initialSearch || '')
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState('')

  useEffect(() => {
    boundsRef.current = bounds
  }, [bounds])

  // A plan that has never been calibrated sits on the default (Birmingham)
  // bounds — offer the event's location straight away instead.
  const isUncalibrated =
    JSON.stringify(toNumberBounds(bounds)) === JSON.stringify(defaultBounds)
  useEffect(() => {
    if (initialSearch && isUncalibrated) runSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (mapRef.current) return

    const center = [
      (Number(bounds.east) + Number(bounds.west)) / 2,
      (Number(bounds.north) + Number(bounds.south)) / 2,
    ]

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
      center,
      zoom: 14,
      attributionControl: true,
    })

    map.addControl(new maplibregl.NavigationControl(), 'bottom-left')
    mapRef.current = map

    return () => {
      map.remove()
      mapRef.current = null
      imageUrlRef.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !imageUrl) return undefined

    const addOverlay = () => {
      if (!map.getSource('event-map-overlay')) {
        map.addSource('event-map-overlay', {
          type: 'image',
          url: imageUrl,
          coordinates: imageCoordinates(bounds),
        })
        map.addLayer({
          id: 'event-map-overlay',
          type: 'raster',
          source: 'event-map-overlay',
          paint: {
            'raster-opacity': opacity,
          },
        })
        imageUrlRef.current = imageUrl
        return
      }

      if (imageUrlRef.current !== imageUrl) {
        map.removeLayer('event-map-overlay')
        map.removeSource('event-map-overlay')
        map.addSource('event-map-overlay', {
          type: 'image',
          url: imageUrl,
          coordinates: imageCoordinates(bounds),
        })
        map.addLayer({
          id: 'event-map-overlay',
          type: 'raster',
          source: 'event-map-overlay',
          paint: {
            'raster-opacity': opacity,
          },
        })
        imageUrlRef.current = imageUrl
      }
    }

    if (map.loaded()) {
      addOverlay()
    } else {
      map.once('load', addOverlay)
    }

    return () => map.off('load', addOverlay)
  }, [imageUrl])

  useEffect(() => {
    const map = mapRef.current
    if (!map?.getSource('event-map-overlay')) return
    map.getSource('event-map-overlay')?.setCoordinates?.(imageCoordinates(bounds))
  }, [bounds])

  useEffect(() => {
    const map = mapRef.current
    if (!map?.getLayer('event-map-overlay')) return
    map.setPaintProperty('event-map-overlay', 'raster-opacity', opacity)
  }, [opacity])

  const useVisibleMapBounds = () => {
    const visible = mapRef.current?.getBounds()
    if (!visible) return
    setBounds({
      north: Number(visible.getNorth().toFixed(6)),
      south: Number(visible.getSouth().toFixed(6)),
      east: Number(visible.getEast().toFixed(6)),
      west: Number(visible.getWest().toFixed(6)),
    })
  }

  const fitToPlan = () => {
    const map = mapRef.current
    if (!map) return
    const current = toNumberBounds(boundsRef.current)
    map.fitBounds(
      [
        [current.west, current.south],
        [current.east, current.north],
      ],
      { padding: 60, duration: 500, maxZoom: 18 },
    )
  }

  // Move the plan (keeping its size) so it's centred on a point, and frame it.
  const movePlanTo = (lng, lat) => {
    const current = toNumberBounds(boundsRef.current)
    const halfLat = Math.abs(current.north - current.south) / 2
    const halfLng = Math.abs(current.east - current.west) / 2
    const next = {
      north: Number((lat + halfLat).toFixed(6)),
      south: Number((lat - halfLat).toFixed(6)),
      east: Number((lng + halfLng).toFixed(6)),
      west: Number((lng - halfLng).toFixed(6)),
    }
    boundsRef.current = next
    setBounds(next)
    fitToPlan()
  }

  const runSearch = async () => {
    const query = searchText.trim()
    if (!query) return
    setSearching(true)
    setSearchError('')
    try {
      const results = await geocodePlace(query)
      setSearchResults(results)
      if (!results.length) setSearchError('No places matched that search.')
    } catch (e) {
      setSearchError('Place search is unavailable right now.')
    } finally {
      setSearching(false)
    }
  }

  // Unlocked: drag moves the plan, wheel resizes it (map pan/zoom paused so
  // the gestures don't fight). Uses native listeners for the wheel so
  // preventDefault actually stops the page scrolling.
  useEffect(() => {
    const map = mapRef.current
    const node = mapContainer.current
    if (!map || !node) return undefined

    if (planLocked) {
      map.dragPan.enable()
      map.scrollZoom.enable()
      node.style.cursor = ''
      return undefined
    }

    map.dragPan.disable()
    map.scrollZoom.disable()
    node.style.cursor = 'move'

    const onPointerDown = (event) => {
      if (event.button !== 0) return
      dragRef.current = {
        pointerId: event.pointerId,
        last: map.unproject([event.offsetX, event.offsetY]),
      }
      node.setPointerCapture?.(event.pointerId)
    }
    const onPointerMove = (event) => {
      const drag = dragRef.current
      if (!drag || drag.pointerId !== event.pointerId) return
      const here = map.unproject([event.offsetX, event.offsetY])
      const dLng = here.lng - drag.last.lng
      const dLat = here.lat - drag.last.lat
      drag.last = here
      const current = toNumberBounds(boundsRef.current)
      const next = {
        north: Number((current.north + dLat).toFixed(6)),
        south: Number((current.south + dLat).toFixed(6)),
        east: Number((current.east + dLng).toFixed(6)),
        west: Number((current.west + dLng).toFixed(6)),
      }
      boundsRef.current = next
      setBounds(next)
    }
    const onPointerUp = (event) => {
      if (dragRef.current?.pointerId !== event.pointerId) return
      node.releasePointerCapture?.(event.pointerId)
      dragRef.current = null
    }
    const onWheel = (event) => {
      event.preventDefault()
      const factor = event.deltaY > 0 ? 0.96 : 1.04
      const current = toNumberBounds(boundsRef.current)
      const centerLat = (current.north + current.south) / 2
      const centerLng = (current.east + current.west) / 2
      const halfLat = ((current.north - current.south) * factor) / 2
      const halfLng = ((current.east - current.west) * factor) / 2
      const next = {
        north: Number((centerLat + halfLat).toFixed(6)),
        south: Number((centerLat - halfLat).toFixed(6)),
        east: Number((centerLng + halfLng).toFixed(6)),
        west: Number((centerLng - halfLng).toFixed(6)),
      }
      boundsRef.current = next
      setBounds(next)
    }

    node.addEventListener('pointerdown', onPointerDown)
    node.addEventListener('pointermove', onPointerMove)
    node.addEventListener('pointerup', onPointerUp)
    node.addEventListener('pointercancel', onPointerUp)
    node.addEventListener('wheel', onWheel, { passive: false })

    return () => {
      node.removeEventListener('pointerdown', onPointerDown)
      node.removeEventListener('pointermove', onPointerMove)
      node.removeEventListener('pointerup', onPointerUp)
      node.removeEventListener('pointercancel', onPointerUp)
      node.removeEventListener('wheel', onWheel)
      map.dragPan.enable()
      map.scrollZoom.enable()
      node.style.cursor = ''
    }
  }, [planLocked])

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          placeholder="Search a place, e.g. Elland Road, Leeds"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              runSearch()
            }
          }}
          sx={{ minWidth: 280, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          onClick={runSearch}
          disabled={searching || !searchText.trim()}
          startIcon={searching ? <CircularProgress size={14} /> : <Search />}
          sx={{ textTransform: 'none', fontWeight: 900 }}
        >
          Find place
        </Button>
        <Button
          variant="outlined"
          onClick={fitToPlan}
          startIcon={<CenterFocusStrong />}
          sx={{ textTransform: 'none', fontWeight: 900 }}
        >
          Show plan
        </Button>
      </Stack>

      {searchError && (
        <Typography variant="caption" color="error">
          {searchError}
        </Typography>
      )}
      {searchResults.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {searchResults.map((result) => (
            <Chip
              key={`${result.lat},${result.lng}`}
              icon={<Place />}
              label={result.label}
              onClick={() => {
                movePlanTo(result.lng, result.lat)
                setSearchResults([])
              }}
              sx={{ maxWidth: 420 }}
            />
          ))}
        </Stack>
      )}

      <Box
        sx={{
          position: 'relative',
          minHeight: 320,
          height,
          borderRadius: 2,
          overflow: 'hidden',
          border: planLocked ? '1px solid #E2E8F0' : '2px solid #239AA4',
        }}
      >
        <Box ref={mapContainer} sx={{ width: '100%', height: '100%' }} />

        <Stack
          spacing={0.75}
          sx={{ position: 'absolute', top: 12, left: 12, maxWidth: 380 }}
        >
          <Button
            variant="contained"
            startIcon={planLocked ? <Lock /> : <LockOpen />}
            onClick={() => setPlanLocked((prev) => !prev)}
            sx={{
              alignSelf: 'flex-start',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 900,
              backgroundColor: planLocked ? 'rgba(15,23,42,0.9)' : '#239AA4',
            }}
          >
            {planLocked ? 'Plan locked' : 'Plan unlocked'}
          </Button>
          <Box
            sx={{
              px: 1.25,
              py: 0.75,
              borderRadius: 2,
              backgroundColor: 'rgba(15,23,42,0.88)',
              color: 'white',
            }}
          >
            <Typography variant="caption" fontWeight={800} color="white">
              {planLocked
                ? 'Locked: drag and scroll to move around the live map — the plan stays pinned where it is. Unlock to move the plan.'
                : 'Unlocked: drag to slide the plan across the live map, scroll to make it bigger or smaller. Lock when it lines up.'}
            </Typography>
          </Box>
        </Stack>

        <Button
          size="small"
          variant="contained"
          onClick={useVisibleMapBounds}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 900,
            backgroundColor: 'rgba(15,23,42,0.9)',
          }}
        >
          Stretch plan to visible area
        </Button>
      </Box>
    </Stack>
  )
}

const EventMapManager = ({
  open,
  onClose,
  controlRoomID,
  eventMap,
  onSaved,
  initialSearch,
  initialCenter,
}) => {
  const fileInput = useRef(null)
  const [file, setFile] = useState(null)
  const [processing, setProcessing] = useState(false)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [existingUrl, setExistingUrl] = useState(null)
  const [drawnToScale, setDrawnToScale] = useState(false)
  const [bounds, setBounds] = useState(
    () => boundsAround(initialCenter) || defaultBounds,
  )
  const [opacity, setOpacity] = useState(0.7)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const displayImageUrl = previewUrl || existingUrl || null
  const canSave = !!file || !!eventMap?.s3key

  useEffect(() => {
    if (!open) return
    setFile(null)
    setPreviewUrl(null)
    setExistingUrl(null)
    setError('')
    setDrawnToScale(!!eventMap?.drawnToScale)
    setBounds(
      toNumberBounds(
        eventMap?.bounds || boundsAround(initialCenter) || defaultBounds,
      ),
    )
    setOpacity(Number.isFinite(Number(eventMap?.opacity)) ? Number(eventMap.opacity) : 0.7)
  }, [open, eventMap])

  useEffect(() => {
    if (!eventMap?.s3key || previewUrl || !open) return
    API.post('/gets3downloadurl', {
      key: eventMap.s3key,
      inlineView: true,
      expires: 300,
    })
      .then((res) => setExistingUrl(res.data))
      .catch(() => {})
  }, [eventMap?.s3key, open, previewUrl])

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const updateBounds = (patch) => {
    setBounds((prev) => ({ ...prev, ...patch }))
  }

  const adjustPosition = (direction) => {
    const current = toNumberBounds(bounds)
    const latSpan = Math.abs(current.north - current.south)
    const lngSpan = Math.abs(current.east - current.west)
    const latStep = latSpan * 0.08
    const lngStep = lngSpan * 0.08

    if (direction === 'up') {
      updateBounds({
        north: Number((current.north + latStep).toFixed(6)),
        south: Number((current.south + latStep).toFixed(6)),
      })
    }
    if (direction === 'down') {
      updateBounds({
        north: Number((current.north - latStep).toFixed(6)),
        south: Number((current.south - latStep).toFixed(6)),
      })
    }
    if (direction === 'left') {
      updateBounds({
        east: Number((current.east - lngStep).toFixed(6)),
        west: Number((current.west - lngStep).toFixed(6)),
      })
    }
    if (direction === 'right') {
      updateBounds({
        east: Number((current.east + lngStep).toFixed(6)),
        west: Number((current.west + lngStep).toFixed(6)),
      })
    }
  }

  const scaleBounds = (factor) => {
    const current = toNumberBounds(bounds)
    const centerLat = (current.north + current.south) / 2
    const centerLng = (current.east + current.west) / 2
    const halfLat = ((current.north - current.south) * factor) / 2
    const halfLng = ((current.east - current.west) * factor) / 2

    setBounds({
      north: Number((centerLat + halfLat).toFixed(6)),
      south: Number((centerLat - halfLat).toFixed(6)),
      east: Number((centerLng + halfLng).toFixed(6)),
      west: Number((centerLng - halfLng).toFixed(6)),
    })
  }

  const handleFile = async (selectedFile) => {
    if (!selectedFile) return
    if (!selectedFile.type.startsWith('image/')) {
      setError('Event maps must be image files.')
      return
    }

    setProcessing(true)
    setError('')
    try {
      const prepared = await prepareEventMapImage(selectedFile)
      if (previewUrl) URL.revokeObjectURL(previewUrl)
      setFile({ name: selectedFile.name, ...prepared })
      setPreviewUrl(URL.createObjectURL(prepared.blob))
    } catch (e) {
      setError(e?.message || 'Unable to read that image.')
    } finally {
      setProcessing(false)
    }
  }

  const imageSize = useMemo(() => {
    if (!file) return eventMap?.imageSize
    return { width: file.width, height: file.height }
  }, [eventMap?.imageSize, file])

  const save = async () => {
    if (!canSave) return
    setSaving(true)
    setError('')

    try {
      let s3key = eventMap?.s3key
      let fileName = eventMap?.fileName
      let contentType = eventMap?.contentType

      if (file) {
        const upload = await API.post(
          `/controlrooms/${controlRoomID}/event-map/upload-url`,
          {
            extension: file.extension,
            size: file.blob.size,
          },
        )
        if (upload.status >= 400) {
          throw new Error(
            upload.data?.message ||
              upload.data?.error ||
              'Unable to prepare the upload.',
          )
        }
        s3key = upload.data.key
        fileName = file.name
        contentType = file.contentType

        // validateStatus never rejects, so a failed S3 PUT would otherwise
        // save metadata pointing at an object that was never written.
        const put = await API.put(upload.data.url, file.blob, {
          headers: {
            'Content-Type': file.contentType,
          },
          timeout: 300000,
          skipGlobalErrorHandler: true,
        })
        if (put.status < 200 || put.status >= 300) {
          throw new Error('The image upload failed. Please try again.')
        }
      }

      const res = await API.post(`/controlrooms/${controlRoomID}/event-map`, {
        s3key,
        fileName,
        contentType,
        drawnToScale,
        bounds: drawnToScale ? bounds : undefined,
        opacity,
        imageSize,
      })
      if (res.status >= 400) {
        throw new Error(
          res.data?.message || res.data?.error || 'Unable to save event map.',
        )
      }

      onSaved?.(res.data)
      onClose()
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Unable to save event map.',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography fontWeight={900}>Event Map</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: '#F8FAFC' }}>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <Button
              variant="contained"
              startIcon={<UploadFile />}
              onClick={() => fileInput.current?.click()}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 900,
                backgroundColor: '#239AA4',
              }}
            >
              Select Event Map
            </Button>
            <input
              ref={fileInput}
              hidden
              type="file"
              accept="image/*"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <Typography variant="body2" color="text.secondary">
              {processing
                ? 'Preparing image…'
                : file?.name || eventMap?.fileName || 'No image selected'}
            </Typography>
            {processing && <CircularProgress size={16} />}
            <Box flex={1} />
            <FormControlLabel
              control={
                <Checkbox
                  checked={drawnToScale}
                  onChange={(e) => setDrawnToScale(e.target.checked)}
                />
              }
              label="Drawn to scale"
            />
          </Stack>

          {drawnToScale ? (
            <Grid container spacing={2}>
              <Grid item xs={12} lg={8}>
                {displayImageUrl ? (
                  <CalibrationMap
                    imageUrl={displayImageUrl}
                    bounds={bounds}
                    setBounds={setBounds}
                    opacity={opacity}
                    initialSearch={initialSearch}
                    height="calc(100vh - 330px)"
                  />
                ) : (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      height: 460,
                      borderRadius: 2,
                      border: '1px dashed #CBD5E1',
                      backgroundColor: 'white',
                    }}
                  >
                    <Typography color="text.secondary">
                      Select an image to calibrate it against the map.
                    </Typography>
                  </Stack>
                )}
              </Grid>
              <Grid item xs={12} lg={4}>
                <Stack spacing={2}>
                  <Alert severity="info">
                    Line the plan up with the real site: search for the place,
                    unlock the plan and drag it into position, scroll to size
                    it, then lock and save. The saved position powers the
                    overlay view and crews' GPS on the plan.
                  </Alert>
                  <Box>
                    <Typography fontWeight={900} gutterBottom>
                      Map transparency
                    </Typography>
                    <Slider
                      value={opacity}
                      min={0.25}
                      max={1}
                      step={0.05}
                      onChange={(_, value) => setOpacity(value)}
                    />
                  </Box>
                  <Grid container spacing={1}>
                    {['north', 'south', 'east', 'west'].map((field) => (
                      <Grid item xs={6} key={field}>
                        <TextField
                          label={field}
                          size="small"
                          fullWidth
                          type="number"
                          value={bounds[field]}
                          onChange={(e) =>
                            updateBounds({ [field]: e.target.value })
                          }
                          inputProps={{ step: '0.000001' }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <Tooltip title="Move left">
                      <IconButton onClick={() => adjustPosition('left')}>
                        <KeyboardArrowLeft />
                      </IconButton>
                    </Tooltip>
                    <Stack spacing={0.5}>
                      <Tooltip title="Move up">
                        <IconButton onClick={() => adjustPosition('up')}>
                          <KeyboardArrowUp />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Move down">
                        <IconButton onClick={() => adjustPosition('down')}>
                          <KeyboardArrowDown />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Tooltip title="Move right">
                      <IconButton onClick={() => adjustPosition('right')}>
                        <KeyboardArrowRight />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Compress />}
                      onClick={() => scaleBounds(0.92)}
                    >
                      Smaller
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Expand />}
                      onClick={() => scaleBounds(1.08)}
                    >
                      Larger
                    </Button>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          ) : (
            <Stack spacing={2}>
              <Alert severity="info">
                Unscaled event maps will use manual event-map positions for
                resources and incidents when drag/drop placement is added.
              </Alert>
              <Box>
                <Typography fontWeight={900} gutterBottom>
                  Map transparency
                </Typography>
                <Slider
                  value={opacity}
                  min={0.25}
                  max={1}
                  step={0.05}
                  onChange={(_, value) => setOpacity(value)}
                />
              </Box>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          onClick={save}
          disabled={saving || processing || !canSave}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 900,
            backgroundColor: '#239AA4',
          }}
        >
          Save Event Map
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EventMapManager
